import { useReducer } from 'react';

import {
  ActivityNotificationEnum,
  DisruptionNotificationEnum,
  DisruptionNotificationGracePeriodEnum,
  type INotificationChangeParams,
  type InputMaybe,
} from 'src/graphql/schema';
import { IAction } from 'src/utils/ts-utilities';

export enum ActionEnum {
  SET_INITIAL_DATA = 'set_initial_data',
  UPDATE_DEPOSIT_DISPLAY = 'update_deposit_display',
  UPDATE_BENEFICIARY_DEPOSIT_NOTIFICATION = 'update_beneficiary_deposit_notification',
  UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION = 'update_beneficiary_disrupt_notification',
  UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION_GRACE = 'update_beneficiary_disrupt_notification_grace',
  UPDATE_DEPOSITOR_DEPOSIT_NOTIFICATION = 'update_depositor_deposit_notification',
  UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION = 'update_depositor_disrupt_notification',
  UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION_GRACE = 'update_depositor_disrupt_notification_grace',
  UPDATE_STORAGE_REGION = 'update_storage_region',
  UPDATE_ASSET_VERSIONS_LIMIT = 'update_asset_versions_limit',
  UPDATE_ACTIVATION_NOTIFICATION = 'update_activation_notification',
}

type IUpdateDepositDisplayPayload =
  | {
      beneficiarySharedInformation: true;
    }
  | (
      | ({
          beneficiarySharedInformation: false;
        } & {
          beneficiaryActivityNotification: InputMaybe<ActivityNotificationEnum>;
          beneficiaryDisruptionNotification: InputMaybe<DisruptionNotificationEnum>;
          beneficiaryDisruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined;
        })
      | {
          depositorActivityNotification: InputMaybe<ActivityNotificationEnum>;
          depositorDisruptionNotification: InputMaybe<DisruptionNotificationEnum>;
          depositorDisruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined;
        }
    );

type IActionReducer =
  | IAction<ActionEnum.SET_INITIAL_DATA, { data: INotificationChangeParams }>
  | IAction<ActionEnum.UPDATE_DEPOSIT_DISPLAY, IUpdateDepositDisplayPayload>
  | IAction<ActionEnum.UPDATE_BENEFICIARY_DEPOSIT_NOTIFICATION, InputMaybe<ActivityNotificationEnum>>
  | IAction<
      ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION,
      {
        disruptionNotification: InputMaybe<DisruptionNotificationEnum>;
        disruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined;
      }
    >
  | IAction<ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION_GRACE, InputMaybe<DisruptionNotificationGracePeriodEnum>>
  | IAction<ActionEnum.UPDATE_DEPOSITOR_DEPOSIT_NOTIFICATION, InputMaybe<ActivityNotificationEnum>>
  | IAction<
      ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION,
      {
        disruptionNotification: InputMaybe<DisruptionNotificationEnum>;
        disruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined;
      }
    >
  | IAction<ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION_GRACE, InputMaybe<DisruptionNotificationGracePeriodEnum>>
  | IAction<ActionEnum.UPDATE_STORAGE_REGION, string>
  | IAction<ActionEnum.UPDATE_ASSET_VERSIONS_LIMIT, InputMaybe<number>>
  | IAction<ActionEnum.UPDATE_ACTIVATION_NOTIFICATION, boolean>;

const initialState: INotificationChangeParams = {
  beneficiarySharedInformation: false,
  beneficiaryActivityNotification: undefined,
  beneficiaryDisruptionNotification: undefined,
  beneficiaryDisruptionNotificationGracePeriod: undefined,
  depositorActivityNotification: undefined,
  depositorDisruptionNotification: undefined,
  depositorDisruptionNotificationGracePeriod: undefined,
  storageRegion: '',
  assetVersionsLimit: undefined,
  activationNotification: false,
};

const reducer = (state: INotificationChangeParams, action: IActionReducer): INotificationChangeParams => {
  switch (action.type) {
    case ActionEnum.SET_INITIAL_DATA:
      return action.payload.data;
    case ActionEnum.UPDATE_DEPOSIT_DISPLAY:
      return { ...state, ...action.payload };
    case ActionEnum.UPDATE_BENEFICIARY_DEPOSIT_NOTIFICATION:
      return {
        ...state,
        beneficiaryActivityNotification: action.payload,
      };
    case ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION:
      return {
        ...state,
        beneficiaryDisruptionNotification: action.payload.disruptionNotification,
        beneficiaryDisruptionNotificationGracePeriod: action.payload.disruptionNotificationGracePeriod,
      };
    case ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION_GRACE: {
      return { ...state, beneficiaryDisruptionNotificationGracePeriod: action.payload };
    }
    case ActionEnum.UPDATE_DEPOSITOR_DEPOSIT_NOTIFICATION:
      return { ...state, depositorActivityNotification: action.payload };
    case ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION:
      return {
        ...state,
        depositorDisruptionNotification: action.payload.disruptionNotification,
        depositorDisruptionNotificationGracePeriod: action.payload.disruptionNotificationGracePeriod,
      };
    case ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION_GRACE: {
      return { ...state, depositorDisruptionNotificationGracePeriod: action.payload };
    }
    case ActionEnum.UPDATE_STORAGE_REGION:
      return { ...state, storageRegion: action.payload };
    case ActionEnum.UPDATE_ASSET_VERSIONS_LIMIT:
      return { ...state, assetVersionsLimit: action.payload };
    case ActionEnum.UPDATE_ACTIVATION_NOTIFICATION:
      return { ...state, activationNotification: action.payload };
    default:
      return state;
  }
};

export const useNotificationsSuggestChangesSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setInitialData = (data: INotificationChangeParams) => {
    dispatch({
      type: ActionEnum.SET_INITIAL_DATA,
      payload: {
        data,
      },
    });
  };

  const updateDepositDisplay = (payload: IUpdateDepositDisplayPayload) =>
    dispatch({ type: ActionEnum.UPDATE_DEPOSIT_DISPLAY, payload });

  const updateBeneficiaryDepositNotification = (depositNotification: InputMaybe<ActivityNotificationEnum>) => {
    if (!depositNotification) return;

    dispatch({
      type: ActionEnum.UPDATE_BENEFICIARY_DEPOSIT_NOTIFICATION,
      payload: depositNotification ? depositNotification : null,
    });
  };

  const updateBeneficiaryDisruptNotification = (
    disruptionNotification: InputMaybe<DisruptionNotificationEnum>,
    disruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined,
  ) =>
    dispatch({
      type: ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION,
      payload: { disruptionNotification, disruptionNotificationGracePeriod },
    });

  const updateBeneficiaryDisruptNotificationGrace = (
    disruptGraceNotification: InputMaybe<DisruptionNotificationGracePeriodEnum>,
  ) =>
    dispatch({
      type: ActionEnum.UPDATE_BENEFICIARY_DISRUPT_NOTIFICATION_GRACE,
      payload: disruptGraceNotification,
    });

  const updateDepositorDepositNotification = (depositNotification: InputMaybe<ActivityNotificationEnum>) => {
    if (!depositNotification) return;

    dispatch({
      type: ActionEnum.UPDATE_DEPOSITOR_DEPOSIT_NOTIFICATION,
      payload: depositNotification,
    });
  };

  const updateDepositorDisruptNotification = (
    disruptionNotification: InputMaybe<DisruptionNotificationEnum>,
    disruptionNotificationGracePeriod: InputMaybe<DisruptionNotificationGracePeriodEnum> | undefined,
  ) =>
    dispatch({
      type: ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION,
      payload: { disruptionNotification, disruptionNotificationGracePeriod },
    });

  const updateDepositorDisruptNotificationGrace = (
    disruptGraceNotification: InputMaybe<DisruptionNotificationGracePeriodEnum>,
  ) =>
    dispatch({
      type: ActionEnum.UPDATE_DEPOSITOR_DISRUPT_NOTIFICATION_GRACE,
      payload: disruptGraceNotification,
    });

  const updateAssetVersionsLimit = (assetVersionsLimit: InputMaybe<number>) =>
    dispatch({
      type: ActionEnum.UPDATE_ASSET_VERSIONS_LIMIT,
      payload: assetVersionsLimit,
    });

  const updateStorageRegion = (storageRegion: string) =>
    dispatch({
      type: ActionEnum.UPDATE_STORAGE_REGION,
      payload: storageRegion,
    });

  const updateActivationNotification = (activationNotification: boolean | null) => {
    if (activationNotification === null) return;

    dispatch({
      type: ActionEnum.UPDATE_ACTIVATION_NOTIFICATION,
      payload: activationNotification,
    });
  };

  return {
    state,
    setInitialData,
    updateDepositDisplay,
    updateBeneficiaryDepositNotification,
    updateBeneficiaryDisruptNotification,
    updateBeneficiaryDisruptNotificationGrace,
    updateDepositorDepositNotification,
    updateDepositorDisruptNotification,
    updateDepositorDisruptNotificationGrace,
    updateStorageRegion,
    updateAssetVersionsLimit,
    updateActivationNotification,
  };
};
